import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { CONCURSOAPI } from '../const';
import { ConcursoPublicadoProps, EtapaLlamadoConcurso } from '../providers/ConcursoProvider';

const sliceName = 'convocatoria';

type State = {
  concursosPublicos: ConcursoPublicadoProps[] | null;
  filtrados: ConcursoPublicadoProps[] | null;
  success: string;
  error: string;
  loading: boolean;
};

const initialState: State = {
  concursosPublicos: null,
  filtrados: null,
  success: '',
  error: '',
  loading: false,
};

type GetEstadoProps = { idEtapaLlamadoConcurso: number };
export const getEstado = createAsyncThunk<EtapaLlamadoConcurso, GetEstadoProps>(
  `${sliceName}/get/estado`,
  async ({ idEtapaLlamadoConcurso }) => {
    const { data } = await axios.get(
      `${CONCURSOAPI}/etapallamadoconcurso/${idEtapaLlamadoConcurso}`,
    );

    return data;
  },
);

export const getPublicados = createAsyncThunk<ConcursoPublicadoProps[]>(
  `${sliceName}/get/concursos`,
  async () => {
    const { data } = await axios.get(`${CONCURSOAPI}/concursos-publicados`);
    return data;
  },
);

type PostPublicarVals = { idLlamadoConcurso: number };
export const postPublicar = createAsyncThunk<void, PostPublicarVals>(
  `${sliceName}/post/publicar`,
  async ({ idLlamadoConcurso }) => {
    await axios.post(`${CONCURSOAPI}/llamadoconcurso/publicar/${idLlamadoConcurso}`);
  },
);

type PostAvanzarVals = { idLlamadoConcurso: number };
export const postAvanzar = createAsyncThunk<void, PostAvanzarVals>(
  `${sliceName}/post/avanzar`,
  async ({ idLlamadoConcurso }) => {
    await axios({
      url: `${CONCURSOAPI}/llamadoconcurso/avanzaretapa/${idLlamadoConcurso}`,
      method: 'POST',
    });
  },
);

type PostRequeGenerales = { idLlamadoConcurso: number; requisitoLlamadoConcurso: string };
export const postRequisitosGenerales = createAsyncThunk<void, PostRequeGenerales>(
  `${sliceName}/post/requisito_general`,
  async ({ idLlamadoConcurso, requisitoLlamadoConcurso }) => {
    await axios.post(`${CONCURSOAPI}/requisitollamadoconcurso/${idLlamadoConcurso}`, {
      descripcion: requisitoLlamadoConcurso,
      nombre: 'Requisito General',
      requerido: true,
    });
  },
);

type ToggleInscripcion = { abrir: boolean; idLlamadoConcurso: number };
export const toggleInscripcion = createAsyncThunk<void, ToggleInscripcion>(
  `${sliceName}/post/toggleInscripcion`,
  async (props) => {
    const { abrir, idLlamadoConcurso } = props;
    const action = abrir ? 'abrir-inscripcion' : 'cerrar-inscripcion';

    await axios.post(`${CONCURSOAPI}/llamadoconcurso/${action}/${idLlamadoConcurso}`);
  },
);

const convocatoriaSlice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    cleanMsg: (state) => ({ ...state, success: '', error: '' }),
    filter: (state, action) => {
      const { payload } = action;
      const { convocatoria, jurisdiccion, niveles, cargoConReserva, estado } = payload;

      const lista = state.concursosPublicos ?? [];

      state.filtrados = lista.filter((concu) => {
        const etapa = concu.etapaLlamadoConcurso?.idEtapaLlamadoConcurso;

        const matchConvocatoria =
          !convocatoria || concu.tipoConcurso.idTipoConcurso === parseInt(convocatoria);

        const matchJurisdiccion = !jurisdiccion || concu.jurisdiccion === jurisdiccion;

        const matchNiveles = !niveles || concu.niveles === niveles;

        const matchReserva = !cargoConReserva || concu.cantidadCargoReserva > 0;

        const matchEstado =
          estado === 'todos' ||
          (estado === 'abierta' && etapa === 9) ||
          (estado === 'cerrada' && etapa > 9) ||
          (estado === 'proxima' && etapa < 9);

        return (
          matchConvocatoria && matchJurisdiccion && matchNiveles && matchReserva && matchEstado
        );
      });
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getPublicados.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getPublicados.rejected, (state) => {
      state.loading = false;
      state.error = 'No se pudieron obtenes los concursos';
    });
    builder.addCase(getPublicados.fulfilled, (state, action) => {
      state.loading = false;
      state.concursosPublicos = action.payload;
    });
    builder.addCase(postAvanzar.fulfilled, (state) => {
      state.success = 'El concurso se envió satisfactoriamente';
      state.loading = false;
    });

    builder.addCase(postPublicar.fulfilled, (state) => {
      state.success = 'La llamada a concurso se publicó correctamente';
      state.loading = false;
    });

    builder.addCase(postAvanzar.rejected, (state) => {
      state.error = 'Ocurrió un error';
      state.loading = false;
    });

    builder.addCase(postPublicar.rejected, (state) => {
      state.error = 'Ocurrió un error';
      state.loading = false;
    });

    builder.addCase(postAvanzar.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(postPublicar.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(postRequisitosGenerales.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(postRequisitosGenerales.fulfilled, (state) => {
      state.success = 'Requisito cargado correctamente';
      state.loading = false;
    });

    builder.addCase(postRequisitosGenerales.rejected, (state) => {
      state.error = 'Hubo un error al cargar el requisito';
      state.loading = false;
    });

    builder.addCase(toggleInscripcion.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(toggleInscripcion.rejected, (state, action) => {
      const que = action.meta.arg.abrir ? 'abrir' : 'cerrar';
      state.error = `No se pudo ${que} la inscripción`;
      state.loading = false;
    });

    builder.addCase(toggleInscripcion.fulfilled, (state, action) => {
      const que = action.meta.arg.abrir ? 'abrió' : 'cerró';
      state.success = `La inscripción se ${que} correctamente`;
      state.loading = false;
    });
  },
});

export const useConvocatoria = () => useSelector((state: any) => state.convocatoria) as State;

export const { cleanMsg, filter } = convocatoriaSlice.actions;
const reducer = convocatoriaSlice.reducer;
export default reducer;
